import { useState, useEffect, useRef, useMemo, useCallback, type ReactNode } from "react";
import ReactDOM from "react-dom";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { Picture } from "assets/icons";
import { ArrowLeft, ArrowRight, Check, DownloadSimple, Question, Trash } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import { FilterBar } from "shared/components/filterBar";
import type { FilterSection } from "shared/components/filterBar/types";
import { PhotoThumbnail, FullScreenView, BulkSecondaryLabelPopover, DeleteConfirmDialog } from "./components";
import LostTeddy from "assets/lost-teddy.jpg";
import { toast } from "react-toastify";
import { downloadFilesZip, DOWNLOAD_ZIP_MAX_FILES } from "shared/api/download-files-zip";

const PAGE_SIZE = 100;
const SELECT_ALL_LIMIT = 50;

const MONTH_OPTIONS = [
  { key: "1", label: "Januari" }, { key: "2", label: "Februari" },
  { key: "3", label: "Maart" }, { key: "4", label: "April" },
  { key: "5", label: "Mei" }, { key: "6", label: "Juni" },
  { key: "7", label: "Juli" }, { key: "8", label: "Augustus" },
  { key: "9", label: "September" }, { key: "10", label: "Oktober" },
  { key: "11", label: "November" }, { key: "12", label: "December" },
];

const SearchTipExample = ({
  query,
  description,
  onTry,
}: {
  query: string;
  description: ReactNode;
  onTry: (query: string) => void;
}) => (
  <div className="rounded-md border border-neutral-primary/10 bg-neutral-primary/[0.02] p-2">
    <div className="flex items-center justify-between gap-2">
      <code className="rounded bg-moss-primary/10 px-1.5 py-0.5 font-mono text-xs text-moss-primary">
        {query}
      </code>
      <button
        type="button"
        onClick={() => onTry(query)}
        className="inline-flex items-center gap-1 rounded-md border border-moss-primary bg-white px-2 py-0.5 text-[11px] font-medium text-moss-primary hover:bg-moss-primary hover:text-white"
      >
        Probeer het uit
      </button>
    </div>
    <p className="mt-1 text-xs text-neutral-secondary-mid">{description}</p>
  </div>
);

const ImagesGallery = ({
  incomingBuildingId,
  incomingUnitId,
  incomingFileLabel,
}: any) => {
  const [images, setImages] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const { session } = useSession();
  const params = useParams();
  const navigate = useNavigate();
  const initialFetch = useRef(true);

  // Search & filter state
  const [searchValue, setSearchValue] = useState("");
  const [selectedYear, setSelectedYear] = useState<string[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string[]>([]);
  const [selectedResourceType, setSelectedResourceType] = useState<string[]>([]);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);

  // Facets for the current filter context (labels + matching building/unit IDs)
  const [labelCounts, setLabelCounts] = useState<{ label: string; count: number }[]>([]);
  const [matchingBuildingIds, setMatchingBuildingIds] = useState<{ id: string; count: number }[]>([]);
  const [matchingUnitIds, setMatchingUnitIds] = useState<{ id: string; count: number }[]>([]);

  // Multi-select state
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [searchTipsOpen, setSearchTipsOpen] = useState(false);

  // Derived from props/params
  const buildingId = params.buildingId || incomingBuildingId || null;
  const unitId = params.unitId || incomingUnitId || null;
  const fileLabel = selectedLabel || incomingFileLabel || null;

  // Show images that have a sharePointFileId (resolved via proxy) or a direct imageUrl fallback
  const visibleImages = useMemo(
    () => images.filter((img) => img.thumbnailUrl || img.imageUrl),
    [images],
  );
  const selectedImage = selectedImageIndex !== null ? visibleImages[selectedImageIndex] : null;
  const hasMore = images.length >= PAGE_SIZE;
  const selectionMode = selectedIds.size > 0;

  const selectedImagesData = useMemo(
    () => visibleImages.filter((img) => selectedIds.has(img.id)),
    [visibleImages, selectedIds],
  );

  // Displayed count label
  const countLabel = useMemo(() => {
    const showing = Math.min((currentPage - 1) * PAGE_SIZE + visibleImages.length, totalCount);
    if (totalCount > 0) return `${showing} van ${totalCount} foto's`;
    return "";
  }, [currentPage, visibleImages.length, totalCount]);

  // Year options (last 6 years)
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 6 }, (_, i) => ({
      key: String(currentYear - i),
      label: String(currentYear - i),
    }));
  }, []);

  const filterSections: FilterSection[] = useMemo(() => [
    {
      key: "resourceType",
      label: "Type",
      options: [
        { key: "building", label: "Gebouw" },
        { key: "unit", label: "Unit" },
      ],
      selectedValues: selectedResourceType,
      onUpdate: (values: string[]) => { setSelectedResourceType(values); setCurrentPage(1); },
      color: "brick" as const,
      singleSelect: true,
    },
    {
      key: "year",
      label: "Jaartal",
      options: yearOptions,
      selectedValues: selectedYear,
      onUpdate: (values: string[]) => { setSelectedYear(values); setCurrentPage(1); },
      color: "moss" as const,
      singleSelect: true,
    },
    {
      key: "month",
      label: "Maand",
      options: MONTH_OPTIONS,
      selectedValues: selectedMonth,
      onUpdate: (values: string[]) => { setSelectedMonth(values); setCurrentPage(1); },
      color: "moss" as const,
      singleSelect: true,
    },
  ], [yearOptions, selectedYear, selectedMonth, selectedResourceType]);

  // Fetch images
  const fetchImages = useCallback(async () => {
    setIsLoading(true);
    setImages([]);

    const queryParams = new URLSearchParams({
      ...(buildingId ? { buildingId } : {}),
      ...(unitId ? { unitId } : {}),
      ...(fileLabel ? { fileLabel } : {}),
      ...(searchValue ? { search: searchValue } : {}),
      ...(selectedYear.length ? { year: selectedYear[0] } : {}),
      ...(selectedMonth.length ? { month: selectedMonth[0] } : {}),
      ...(selectedResourceType.length ? { resourceType: selectedResourceType[0] } : {}),
      page: currentPage.toString(),
      pageSize: PAGE_SIZE.toString(),
    }).toString();

    try {
      const response = await fetchWithAuth(
        `${API_URL}/file/get-images?${queryParams}`,
        { method: "GET" },
        session,
      );
      const result = await response.json();
      const data = result.data || result;
      setImages(data);
      if (result.totalCount != null) {
        setTotalCount(result.totalCount);
      }
    } catch (error) {
      console.error("Error fetching images:", error);
    } finally {
      setIsLoading(false);
    }
  }, [buildingId, unitId, fileLabel, searchValue, selectedYear, selectedMonth, selectedResourceType, currentPage, session]);

  // Fetch facets (labels + matching building/unit IDs) for the active filter context.
  // Uses the same filters as the photo query so counts reflect actual filtered results.
  useEffect(() => {
    if (!buildingId && !unitId && !searchValue && !selectedYear.length && !selectedMonth.length && !selectedResourceType.length) {
      setLabelCounts([]);
      setMatchingBuildingIds([]);
      setMatchingUnitIds([]);
      return;
    }
    const controller = new AbortController();
    async function fetchFacets() {
      try {
        const queryParams = new URLSearchParams({
          ...(buildingId ? { buildingId } : {}),
          ...(unitId ? { unitId } : {}),
          ...(fileLabel ? { fileLabel } : {}),
          ...(searchValue ? { search: searchValue } : {}),
          ...(selectedYear.length ? { year: selectedYear[0] } : {}),
          ...(selectedMonth.length ? { month: selectedMonth[0] } : {}),
          ...(selectedResourceType.length ? { resourceType: selectedResourceType[0] } : {}),
        }).toString();
        const response = await fetchWithAuth(
          `${API_URL}/file/picture-facets?${queryParams}`,
          { method: "GET", signal: controller.signal },
          session,
        );
        const data = await response.json();
        setLabelCounts(data.labels || []);
        setMatchingBuildingIds(data.buildingIds || []);
        setMatchingUnitIds(data.unitIds || []);
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          console.error("Error fetching picture facets:", error);
        }
      }
    }
    fetchFacets();
    return () => controller.abort();
  }, [buildingId, unitId, fileLabel, searchValue, selectedYear, selectedMonth, selectedResourceType, session]);

  // Initial fetch
  useEffect(() => {
    if (initialFetch.current) {
      initialFetch.current = false;
      fetchImages();
      return;
    }
  }, []);

  // Refetch when filters change
  useEffect(() => {
    if (initialFetch.current) return;
    fetchImages();
  }, [buildingId, unitId, fileLabel, searchValue, selectedYear, selectedMonth, selectedResourceType, currentPage]);

  const softDelete = (imageId: any) => {
    setImages((prev) => prev.filter((img) => img.id !== imageId));
    setTotalCount((prev) => Math.max(0, prev - 1));
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setSelectedLabel(null);
    setCurrentPage(1);
  };

  const handleTryExample = useCallback((query: string) => {
    handleSearchChange(query);
    setSearchTipsOpen(false);
    toast.info(`Zoekopdracht ingesteld: "${query}"`);
  }, []);

  const handlePreviousPage = () => setCurrentPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setCurrentPage((p) => p + 1);

  const handleImageUpdate = (updatedImage: any) => {
    setImages((prev) => prev.map((img) => img.id === updatedImage.id ? { ...img, ...updatedImage } : img));
  };

  // Selection handlers
  const handleToggleSelect = (imageId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(imageId)) next.delete(imageId);
      else next.add(imageId);
      return next;
    });
  };

  const handleClearSelection = () => setSelectedIds(new Set());

  const handleSelectVisible = useCallback(() => {
    if (visibleImages.length === 0) {
      toast.info("Geen foto's om te selecteren");
      return;
    }
    const ids = visibleImages
      .slice(0, SELECT_ALL_LIMIT)
      .map((img) => img.id as number);
    setSelectedIds(new Set(ids));
    if (visibleImages.length > SELECT_ALL_LIMIT) {
      toast.info(
        `Eerste ${SELECT_ALL_LIMIT} foto's geselecteerd (van ${visibleImages.length} in beeld)`,
      );
    } else {
      toast.info(
        `${ids.length} ${ids.length === 1 ? "foto" : "foto's"} geselecteerd`,
      );
    }
  }, [visibleImages]);

  const handleDownloadSelection = useCallback(async () => {
    if (!session || selectedIds.size === 0 || isDownloadingZip) return;
    const ids = Array.from(selectedIds);
    if (ids.length > DOWNLOAD_ZIP_MAX_FILES) {
      toast.error(
        `Je kan maximaal ${DOWNLOAD_ZIP_MAX_FILES} foto's tegelijk downloaden`,
      );
      return;
    }

    setIsDownloadingZip(true);
    const loadingToast = toast.info(
      `ZIP wordt voorbereid (${ids.length} ${ids.length === 1 ? "foto" : "foto's"})...`,
      { autoClose: false },
    );
    try {
      const { includedCount, skippedCount } = await downloadFilesZip(ids, session);
      toast.dismiss(loadingToast);
      if (skippedCount > 0) {
        toast.warning(
          `${includedCount} ${includedCount === 1 ? "foto" : "foto's"} gedownload, ${skippedCount} overgeslagen`,
        );
      } else {
        toast.success(
          `${includedCount} ${includedCount === 1 ? "foto" : "foto's"} gedownload`,
        );
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error("Error downloading zip:", error);
      toast.error(error instanceof Error ? error.message : "Download mislukt");
    } finally {
      setIsDownloadingZip(false);
    }
  }, [selectedIds, session, isDownloadingZip]);

  const handleBulkLabelDone = (updatedImages: any[]) => {
    setImages((prev) =>
      prev.map((img) => {
        const updated = updatedImages.find((u) => u.id === img.id);
        return updated ? { ...img, secondaryLabels: updated.secondaryLabels } : img;
      }),
    );
  };

  const handleBulkDelete = useCallback(async () => {
    if (selectedIds.size === 0 || isBulkDeleting || !session) return;
    setShowBulkDeleteConfirm(false);
    setIsBulkDeleting(true);

    const ids = Array.from(selectedIds);
    const loadingToast = toast.info(
      `${ids.length} foto's worden verwijderd...`,
      { autoClose: false },
    );

    const deletedIds: number[] = [];
    let failCount = 0;

    for (const id of ids) {
      const image = visibleImages.find((img) => img.id === id);
      if (!image) continue;
      try {
        const res = await fetchWithAuth(
          `${API_URL}/file`,
          { method: "DELETE", headers: { "Content-Type": "application/json" }, body: JSON.stringify(image) },
          session,
        );
        if (res.ok) {
          deletedIds.push(id);
        } else {
          failCount++;
        }
      } catch {
        failCount++;
      }
    }

    toast.dismiss(loadingToast);

    if (deletedIds.length > 0) {
      setImages((prev) => prev.filter((img) => !deletedIds.includes(img.id)));
      setTotalCount((prev) => Math.max(0, prev - deletedIds.length));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        deletedIds.forEach((id) => next.delete(id));
        return next;
      });
    }

    if (failCount === 0) {
      toast.success(`${deletedIds.length} foto's verwijderd`);
    } else {
      toast.warning(`${deletedIds.length} verwijderd, ${failCount} mislukt`);
    }

    setIsBulkDeleting(false);
  }, [selectedIds, visibleImages, isBulkDeleting, session]);

  // Fullscreen navigation
  const handlePrevImage = useCallback(() => {
    if (selectedImageIndex !== null && selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1);
    }
  }, [selectedImageIndex]);

  const handleNextImage = useCallback(() => {
    if (selectedImageIndex !== null && selectedImageIndex < visibleImages.length - 1) {
      setSelectedImageIndex(selectedImageIndex + 1);
    }
  }, [selectedImageIndex, visibleImages.length]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const PaginationControls = () => (
    <div className="flex items-center gap-2">
      <button
        onClick={handlePreviousPage}
        disabled={currentPage === 1}
        className={`rounded-full p-1.5 text-white ${currentPage === 1 ? "bg-moss-primary/40" : "bg-moss-primary hover:bg-moss-primary/80"}`}
      >
        <ArrowLeft size={16} />
      </button>
      <span className="text-sm font-medium text-neutral-primary">
        {currentPage} / {totalPages}
      </span>
      <button
        onClick={handleNextPage}
        disabled={!hasMore}
        className={`rounded-full p-1.5 text-white ${!hasMore ? "bg-moss-primary/40" : "bg-moss-primary hover:bg-moss-primary/80"}`}
      >
        <ArrowRight size={16} />
      </button>
    </div>
  );

  const GRID_CLASSES = "grid grid-cols-3 gap-0.5 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8";

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Foto's &mdash; Yallyhub</title>
        </Helmet>
      </HelmetProvider>

      <div className="z-10 flex flex-col gap-4 pb-20">
        {/* Header */}
        <div className="bg-brick-secondary-fine sticky top-0 z-20 flex items-center gap-2 py-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Terug"
            className="text-neutral-secondary-mid hover:text-brick-primary inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors hover:bg-neutral-primary/5"
          >
            <ArrowLeft size={20} weight="bold" />
          </button>
          <Picture className="h-5 w-5" />
          <p className="text-lg font-light">Foto's</p>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={handleSelectVisible}
              disabled={visibleImages.length === 0}
              className="inline-flex items-center gap-1.5 rounded-md border border-moss-primary bg-white px-2.5 py-1 text-xs font-medium text-moss-primary hover:bg-moss-primary/10 disabled:cursor-not-allowed disabled:opacity-50"
              title={`Selecteert de eerste ${SELECT_ALL_LIMIT} foto's in beeld`}
            >
              <Check size={14} weight="bold" />
              Selecteer alles
            </button>
            <Popover open={searchTipsOpen} onOpenChange={setSearchTipsOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 rounded-md border border-neutral-primary/20 bg-white px-2.5 py-1 text-xs font-medium text-neutral-primary hover:bg-neutral-primary/5"
                  title="Zoektips"
                >
                  <Question size={14} weight="bold" />
                  Zoektips
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-[400px] p-4">
                <div className="flex flex-col gap-3 text-sm text-neutral-primary">
                  <div>
                    <p className="mb-1 font-semibold">Slim zoeken in foto's</p>
                    <p className="text-xs text-neutral-secondary-mid">
                      De zoekbalk doorzoekt gebouw-ID, unit-ID, labels, locatie,
                      beschrijving én secundaire labels tegelijk.
                    </p>
                  </div>
                  <div className="flex flex-col gap-2.5">
                    <SearchTipExample
                      query="badkamer, keuken"
                      description={
                        <>
                          Komma = OF. Toont foto's met "badkamer" <em>of</em>{" "}
                          "keuken".
                        </>
                      }
                      onTry={handleTryExample}
                    />
                    <SearchTipExample
                      query="2000-EMIE & badkamer"
                      description="& = EN. Combineer termen — hier: gebouw EN label."
                      onTry={handleTryExample}
                    />
                    <SearchTipExample
                      query="2000 EMIE 12"
                      description={
                        <>
                          Spaties, streepjes of punten zijn uitwisselbaar —
                          matcht ook <code>2000-EMIE-12-2-V1</code>.
                        </>
                      }
                      onTry={handleTryExample}
                    />
                    <SearchTipExample
                      query="roeselare"
                      description="Werkt ook op adres/locatie van de foto."
                      onTry={handleTryExample}
                    />
                    <SearchTipExample
                      query="keuken & 2000, badkamer & 8800"
                      description="Combineer alles: meerdere AND-groepen, gescheiden door komma's."
                      onTry={handleTryExample}
                    />
                  </div>
                  <p className="text-[11px] text-neutral-secondary-mid">
                    Tip: gebruik de filterknoppen voor jaar/maand/type; de
                    zoekbalk voor alles daarbuiten.
                  </p>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Bulk action bar when images are selected */}
        {selectionMode && (
          <div className="sticky top-12 z-30 flex items-center gap-3 rounded-lg border border-moss-primary/30 bg-white px-4 py-2 shadow-sm">
            <span className="text-sm font-medium text-neutral-primary">
              {selectedIds.size} foto{'\''}s geselecteerd
            </span>
            <BulkSecondaryLabelPopover
              selectedImages={selectedImagesData}
              onDone={handleBulkLabelDone}
            />
            <button
              onClick={handleDownloadSelection}
              disabled={isDownloadingZip || isBulkDeleting}
              className="inline-flex items-center gap-1.5 rounded-md bg-moss-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-moss-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <DownloadSimple size={16} />
              {isDownloadingZip ? "Downloaden..." : "Download selectie"}
            </button>
            <button
              onClick={() => setShowBulkDeleteConfirm(true)}
              disabled={isBulkDeleting || isDownloadingZip}
              className="inline-flex items-center gap-1.5 rounded-md border border-status-bad px-3 py-1.5 text-sm font-medium text-status-bad hover:bg-status-bad hover:text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Trash size={16} />
              {isBulkDeleting ? "Verwijderen..." : "Verwijder selectie"}
            </button>
            <button
              onClick={handleClearSelection}
              className="ml-auto text-sm text-neutral-secondary-mid hover:text-neutral-primary"
            >
              Deselecteer alles
            </button>
          </div>
        )}

        {/* FilterBar */}
        <FilterBar
          searchValue={searchValue}
          onSearchChange={handleSearchChange}
          searchPlaceholder="Zoek op gebouw, unit, label, locatie, beschrijving..."
          filterSections={filterSections}
          showWhenEmpty={true}
          isLoading={isLoading}
          contextLabel={countLabel || undefined}
          rightContent={<PaginationControls />}
        />

        {/* Label chips */}
        {labelCounts.filter((lc) => lc.count > 0).length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {labelCounts
              .filter((lc) => lc.count > 0)
              .map(({ label, count }) => {
                const searchTerms = searchValue
                  ? searchValue.split(/[,&]/).map((t) => t.trim().toLowerCase()).filter(Boolean)
                  : [];
                const matchesSearch = searchTerms.length > 0 &&
                  searchTerms.some((term) => label.toLowerCase().includes(term));
                const isActive = selectedLabel === label;

                return (
                  <button
                    key={label}
                    onClick={() => {
                      setSelectedLabel(isActive ? null : label);
                      setCurrentPage(1);
                    }}
                    className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                      isActive
                        ? "bg-moss-primary text-white"
                        : matchesSearch
                          ? "bg-moss-primary/80 text-white"
                          : "border border-moss-primary bg-moss-primary/10 text-moss-primary hover:bg-moss-primary/20"
                    }`}
                  >
                    {label} ({count})
                  </button>
                );
              })}
            {selectedLabel && (
              <button
                onClick={() => { setSelectedLabel(null); setCurrentPage(1); }}
                className="rounded-full border border-status-bad/30 bg-status-bad/10 px-2.5 py-1 text-xs font-medium text-status-bad hover:bg-status-bad/20"
              >
                Wis label filter
              </button>
            )}
          </div>
        )}

        {/* Building ID suggestions — clicking refines search to that building */}
        {matchingBuildingIds.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-medium text-neutral-secondary-mid uppercase tracking-wide mr-1">
              Gebouwen ({matchingBuildingIds.length}):
            </span>
            {matchingBuildingIds.map(({ id, count }) => {
              const isInSearch = searchValue.toLowerCase().includes(id.toLowerCase());
              return (
                <button
                  key={`b-${id}`}
                  onClick={() => handleSearchChange(id)}
                  className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                    isInSearch
                      ? "bg-brick-primary text-white"
                      : "border border-brick-primary/60 bg-brick-primary/10 text-brick-primary hover:bg-brick-primary/20"
                  }`}
                >
                  {id} ({count})
                </button>
              );
            })}
          </div>
        )}

        {/* Unit ID suggestions — shown when there's a manageable number */}
        {matchingUnitIds.length > 0 && matchingUnitIds.length <= 30 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-medium text-neutral-secondary-mid uppercase tracking-wide mr-1">
              Units ({matchingUnitIds.length}):
            </span>
            {matchingUnitIds.map(({ id, count }) => {
              const isInSearch = searchValue.toLowerCase().includes(id.toLowerCase());
              return (
                <button
                  key={`u-${id}`}
                  onClick={() => handleSearchChange(id)}
                  className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                    isInSearch
                      ? "bg-yellow-600 text-white"
                      : "border border-yellow-600/60 bg-yellow-600/10 text-yellow-700 hover:bg-yellow-600/20"
                  }`}
                >
                  {id} ({count})
                </button>
              );
            })}
          </div>
        )}

        {/* Gallery grid */}
        {isLoading ? (
          <div className={GRID_CLASSES}>
            {Array.from({ length: 40 }).map((_, i) => (
              <div key={i} className="aspect-square animate-pulse rounded-lg bg-neutral-200" />
            ))}
          </div>
        ) : visibleImages.length > 0 ? (
          <div className={GRID_CLASSES}>
            {visibleImages.map((image: any, index: number) => (
              <PhotoThumbnail
                key={image.id}
                image={image}
                onClick={() => setSelectedImageIndex(index)}
                isSelected={selectedIds.has(image.id)}
                onSelect={() => handleToggleSelect(image.id)}
                selectionMode={selectionMode}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16">
            <img className="max-w-[300px] opacity-60" src={LostTeddy} alt="Geen resultaten" />
            <p className="mt-4 text-sm text-neutral-secondary-mid">
              Geen foto's gevonden
            </p>
          </div>
        )}
      </div>

      <DeleteConfirmDialog
        isOpen={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        onConfirm={handleBulkDelete}
        description={`Wil je ${selectedIds.size} geselecteerde foto's verwijderen? Dit kan niet ongedaan worden gemaakt en verwijdert de foto's ook van SharePoint.`}
        confirmLabel={`Verwijder ${selectedIds.size} foto's`}
      />

      {/* Fullscreen view via portal */}
      {selectedImage && ReactDOM.createPortal(
        <FullScreenView
          image={selectedImage}
          onClose={() => setSelectedImageIndex(null)}
          onDelete={(id) => { softDelete(id); setSelectedImageIndex(null); }}
          onUpdate={handleImageUpdate}
          onPrev={selectedImageIndex! > 0 ? handlePrevImage : undefined}
          onNext={selectedImageIndex! < visibleImages.length - 1 ? handleNextImage : undefined}
          currentIndex={selectedImageIndex!}
          totalInPage={visibleImages.length}
        />,
        document.body,
      )}
    </>
  );
};

export default ImagesGallery;
